// A workload badge that fetches its own week (EMR-294, layer 3).

import { useSchedulingPointLoads } from '../../hooks/useSchedulingPointLoads';
import { ClinicianLoadBadge } from './ClinicianLoadBadge';

export interface SchedulingPointLoadAnnotationProps {
  clinicId: string | null | undefined;
  /** Any date in the week of interest, "YYYY-MM-DD". */
  date: string | null | undefined;
  userId: string | null | undefined;
  enabled?: boolean;
}

/**
 * The badge, connected, for call sites that cannot hoist a hook — chiefly the
 * task table, where each row's dropdown belongs to a different day and the
 * rows are produced by a `.map`.
 *
 * One instance per *option* rather than per row, which looks like N requests
 * and is not: every option in a row shares one clinic and one date, so they
 * share one query key and Query serves them all from a single fetch.
 *
 * **Mounting is what pays for the request, so mount it late.** The task table
 * reaches this through react-select's `formatOptionLabel`, which builds option
 * rows only while the menu is open — nothing is fetched until a scheduler
 * opens a picker, and closing it stops the cost. `enabled` is for the caller's
 * own reasons to stay quiet (no permission, a dialog that is shut); it is not
 * an open/closed signal, and there is no need to supply one here.
 *
 * The Add Task dialog does not use this component. It hoists the hook once and
 * passes the result to `ClinicianLoadBadge` through `DropdownField`'s
 * annotation slot — Radix keeps closed `SelectContent` in a detached fragment,
 * so an option-mounted query there would fire on page load rather than on open
 * (measured: nine tasks, nine requests, no dropdown touched).
 */
export const SchedulingPointLoadAnnotation = ({
  clinicId,
  date,
  userId,
  enabled = true,
}: SchedulingPointLoadAnnotationProps) => {
  const { week, loadsByUserId } = useSchedulingPointLoads({
    clinicId,
    date,
    enabled,
  });

  return (
    <ClinicianLoadBadge
      week={week}
      loadsByUserId={loadsByUserId}
      userId={userId}
    />
  );
};

export default SchedulingPointLoadAnnotation;
